/** Quarterly recurrence options for an assigned post-it. */
import type {
  QuarterlyOptions as QuarterlyOptionsType,
  QuarterNumber,
  PostItColor,
} from '@/types/post-it.types';
import { cn } from '@/lib/utils';
import {
  TEXT_COLOR_MAP,
  BORDER_COLOR_MAP,
  POST_IT_QUARTER_SHORT_OPTIONS,
} from '@/components/post-its/post-it-configs';
import {
  getScheduleChipClasses,
  isQuarterActive,
  toggleAllQuarters,
  toggleTargetQuarter,
} from '@/components/post-its/post-it-form-utils';

/** Props for {@link QuarterlyOptions}. */
interface QuarterlyOptionsProps {
  value: QuarterlyOptionsType;
  onChange: (value: QuarterlyOptionsType) => void;
  color?: PostItColor;
}

/** Quarterly recurrence options for an assigned post-it. */
export function QuarterlyOptions({
  value,
  onChange,
  color = 'jaune',
}: Readonly<QuarterlyOptionsProps>) {
  const textColor = TEXT_COLOR_MAP[color] || TEXT_COLOR_MAP.jaune;
  const borderColor = BORDER_COLOR_MAP[color] || BORDER_COLOR_MAP.jaune;

  const handleQuarterToggle = (quarter: QuarterNumber) => {
    onChange({ ...value, targetQuarters: toggleTargetQuarter(value.targetQuarters, quarter) });
  };

  return (
    <div className="space-y-3">
      <div className="space-y-1">
        <div className="flex items-center justify-between">
          <p className={cn('text-xs font-medium', textColor)}>Trimestres ciblés</p>
          <button
            type="button"
            onClick={() =>
              onChange({ ...value, targetQuarters: toggleAllQuarters(value.targetQuarters) })
            }
            className={cn(
              'text-xs px-2 py-0.5 rounded border transition-all',
              borderColor,
              getScheduleChipClasses(color, value.targetQuarters === 'all'),
            )}
          >
            Tous
          </button>
        </div>
        <div className="flex flex-wrap gap-1">
          {POST_IT_QUARTER_SHORT_OPTIONS.map((q) => {
            const active = isQuarterActive(value.targetQuarters, q.value);
            return (
              <button
                key={q.value}
                type="button"
                onClick={() => handleQuarterToggle(q.value)}
                className={cn(
                  'w-12 py-1 rounded text-xs border transition-all',
                  borderColor,
                  getScheduleChipClasses(color, active),
                )}
              >
                {q.label}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
